import React, { Fragment } from 'react';
import { Route, Switch, RouteComponentProps, Redirect } from 'react-router';
import _ from 'lodash';
import './MainPage.scss';

import { Reports } from 'containers/Reports';
import { Accounts } from 'containers/Accounts';
import { Agencies } from 'containers/Agencies';
import { AppHeader } from 'components/AppHeader';
import { AppMenuBar } from 'components/AppMenuBar';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { FinalReportPage } from 'pages/FinalReportPage/FinalReportPage';

import { MainPageProps, MainPageState } from './MainPageModel';
import { Orders } from 'containers/Orders/Orders';
import { ErrorPage } from '../ErrorPage/ErrorPage';
import MenuBarExpandContext from 'contexts/menuBarExpandContext';
import { Creatives } from 'containers/Creatives/Creatives';
import Swagger from 'containers/Swagger/Swagger';
import { CampaignRedirect } from 'containers/L2Objects/CampaignRedirect';
import { L1ObjectRedirect } from 'containers/L1Objects/L1ObjectRedirect';
import { CreativeRedirect } from 'containers/Creatives/CreativeRedirect';
import { BindingRedirect } from 'components/CampaignCreativeManagement/BindingRedirect';
import { Advertisers } from 'containers/Advertisers/Advertisers';
import { Conversions } from 'containers/Conversions/Conversions';
import PermissionContext from 'contexts/permissionContext';
import {
  addOnEnabled,
  hasFuncs,
  isRoles,
  notRoles
} from 'core/permission/PermissionDSL';
import RouteWithPermission from 'components/RouteWithPermission/RouteWithPermission';
import { Permission } from 'core/auth/Permission';
import { ROUTE_PATH } from 'enum/RoutePath';
import { Doctors } from 'containers/Doctors/Doctors';
import { DefaultDoctorsModel } from 'containers/Doctors/DoctorsModel';
import { RoleNames } from 'core';
import { ProductGroups } from 'containers/ProductGroups/ProductGroups';
import { CurrencyRateList } from 'containers/CurrencyRates/CurrencyRateList';
import CoreContext from 'contexts/coreContext';
import { Segments } from 'containers/Segments/Segments';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import { StoredValue } from 'containers/StoredValue/StoredValue';
import { TenmaxSegments } from 'containers/TenmaxSegments/TenmaxSegments';
import { DeviceManagement } from 'containers/DeviceManagement/DeviceManagement';
import { ElementScrollControl } from 'components/ElementScrollControl/ElementScrollControl';
import { CustomLayouts } from 'containers/CustomLayout/CustomLayouts';
import { Pmp } from 'containers/PmpOrder/Pmp';
import { ChannelGroups } from 'containers/ChannelGroups/ChannelGroups';
import { StreamingStatus } from 'containers/StreamingStatus/StreamingStatus';
import { BeaconMessages } from 'containers/BeaconMessages/BeaconMessages';
import { Restaurants } from 'containers/Restaurants/Restaurants';
import { Merchants } from 'containers/Merchants/Merchants';
import { SiteAnnouncements } from 'containers/SiteAnnouncement/SiteAnnouncements';
import { RedirectAnnouncement } from 'components/TopNotification/RedirectAnnouncement';

export class MainPage extends React.Component<
  RouteComponentProps & MainPageProps,
  MainPageState
> {
  handler?: number;

  constructor(props: any) {
    super(props);
    this.state = this.props.model.state;
  }

  componentDidMount() {
    this.props.model.init();
    this.handler = this.props.model.event.add((model) => {
      this.setState(model.state);
    });
  }

  componentWillUnmount() {
    this.handler && this.props.model.event.remove(this.handler);
  }

  componentDidUpdate(_1, prevState: MainPageState) {
    if (
      prevState.permissionContext.actor !==
      this.props.model.state.permissionContext.actor
    ) {
      this.removeErrorCodeOnActorChange();
    }
  }

  removeErrorCodeOnActorChange() {
    const errorCodeReg = /\/(?:error403|error404)$/;
    if (this.props.location.pathname.match(errorCodeReg)) {
      this.props.history.replace(
        this.props.location.pathname.replace(errorCodeReg, '')
      );
    }
  }

  renderAccount = () => {
    return <Accounts model={this.props.model.accountsModel} />;
  };

  renderOEMAccount = () => {
    return <Accounts model={this.props.model.oemAccountsModel} />;
  };

  renderOrders = () => {
    return <Orders model={this.props.model.ordersModel} />;
  };

  renderCreatives = (props) => {
    return <Creatives {...props} model={this.props.model.creativesModel} />;
  };

  renderReports = (props) => {
    return <Reports {...props} core={this.props.model.core} />;
  };

  renderSwagger = () => {
    const authenticationManager =
      this.props.model.core && this.props.model.core.authenticationManager;
    return <Swagger authenticationManager={authenticationManager} />;
  };

  renderAgencies = () => {
    return <Agencies model={this.props.model.agenciesModel} />;
  };

  renderAudienceSegments = () => {
    return <Segments model={this.props.model.segmentsModel} />;
  };

  redirectToSource = () => {
    return <Redirect to='/tenmax-segments/sources' />;
  };

  renderTenmaxSegments = () => {
    return <TenmaxSegments />;
  };

  renderDeviceManagement = () => {
    return <DeviceManagement />;
  };

  renderConversions = (props) => {
    return <Conversions {...props} model={this.props.model.conversionsModel} />;
  };

  renderCampaignDetail = (props) => {
    const query = new URLSearchParams(props.location.search);
    const l1ObjectId = query.get('l1ObjectId');
    const campaignId = props.match.params.campaignId;
    if (!l1ObjectId) {
      return <Redirect to='/' />;
    }
    return <CampaignRedirect l1ObjectId={l1ObjectId} campaignId={campaignId} />;
  };

  renderL1ObjectDetail = (props) => {
    const l1ObjectId = props.match.params.l1ObjectId;
    return <L1ObjectRedirect l1ObjectId={l1ObjectId} />;
  };

  renderCreativeDetail = (props) => {
    const creativeId = props.match.params.creativeId;
    return <CreativeRedirect creativeId={creativeId} />;
  };

  renderBindingDetail = (props) => {
    const query = new URLSearchParams(props.location.search);
    const l1ObjectId = query.get('l1ObjectId');
    const campaignId = query.get('campaignId');
    if (!l1ObjectId || !campaignId) {
      return <Redirect to='/' />;
    }
    return <BindingRedirect l1ObjectId={l1ObjectId} campaignId={campaignId} />;
  };

  renderAppMenuBar = () => {
    return <AppMenuBar model={this.props.model.barModel} />;
  };

  renderAdvertisers = () => {
    return <Advertisers model={this.props.model.advertisersModel} />;
  };

  renderErrorPage = (errorCode) => {
    const onBackClicked = (path: string) => {
      this.props.history.push(path);
    };
    return (
      <ErrorPage
        errorCode={errorCode}
        onBackClicked={onBackClicked}
        currentPath={this.props.location.pathname}
      />
    );
  };

  renderMainPage = () => {
    const mainPagePath = this.props.model.mainPagePath;
    return <Redirect to={mainPagePath} />;
  };

  renderDoctors = () => {
    return <Doctors model={new DefaultDoctorsModel()} />;
  };

  renderProductGroups = () => {
    return <ProductGroups />;
  };

  renderCurrencyRate = () => {
    return <CurrencyRateList />;
  };

  renderChannelGroups = () => {
    return <ChannelGroups />;
  };

  renderStreamingStatus = () => {
    return <StreamingStatus />;
  };

  renderStoredValue = () => {
    return <StoredValue />;
  };

  renderCustomLayouts = () => {
    return <CustomLayouts />;
  };

  renderPmp = () => {
    return <Pmp />;
  };

  renderMerchants = () => {
    return <Merchants />;
  };

  renderRestaurants = () => {
    return <Restaurants model={this.props.model.agenciesModel} />;
  };

  renderBeaconMessages = () => {
    return <BeaconMessages />;
  };

  renderSiteAnnouncements = () => {
    return <SiteAnnouncements />;
  };

  renderFinalReportPageContent = (props) => {
    const indicator = this.state.loading ? <LoadingIndicator /> : null;
    const model = this.props.model;
    const account = model.account;

    return account === null || indicator ? (
      indicator
    ) : (
      <FinalReportPage
        {...props}
        authenticationManager={this.props.authenticationManager}
      />
    );
  };

  renderMainPageContent = () => {
    const indicator = this.state.loading ? <LoadingIndicator /> : null;
    const model = this.props.model;
    const account = model.account;
    const {
      sysAdmin,
      agencyReport,
      adsReport,
      agencyAdmin,
      agencyOperator,
      agencyManager,
      beaconAdmin,
      beaconExecutive
    } = RoleNames;
    const renderEmpty = () => <div />;

    return (
      <div className='main-page'>
        <AppHeader model={model.headerModel} />
        <div className='main-page-container'>
          {account === null || indicator ? (
            indicator
          ) : (
            <Fragment>
              <Switch>
                <Route
                  path={`*/${ROUTE_PATH.ERROR403}`}
                  render={this.renderAppMenuBar}
                />
                <Route
                  path={`*/${ROUTE_PATH.ERROR404}`}
                  render={this.renderAppMenuBar}
                />
                <Route
                  path='/orders/:orderNumber/campaigns'
                  render={renderEmpty}
                />
                <Route
                  path='/orders/:orderNumber/campaign-groups/:l1ObjectId/campaigns'
                  render={renderEmpty}
                />
                <Route
                  path='/orders/:orderNumber/campaign-groups/:l1ObjectId/drafts'
                  render={renderEmpty}
                />
                <Route path='/creatives/new' render={renderEmpty} />
                <Route
                  path='/creatives/:creativeId/edit'
                  render={renderEmpty}
                />
                <Route path='/beacon-messages/new' render={renderEmpty} />
                <Route
                  path='/beacon-messages/:beaconMessageId/edit'
                  render={renderEmpty}
                />
                <Route
                  path='/beacon-messages/:beaconMessageId/summary'
                  render={renderEmpty}
                />
                <Route path='/custom-layouts/new' render={renderEmpty} />
                <Route render={this.renderAppMenuBar} />
              </Switch>
              <div className='content'>
                <RedirectAnnouncement />
                <ElementScrollControl>
                  <Fragment>
                    <Switch>
                      <Route
                        path={`*/${ROUTE_PATH.ERROR403}`}
                        component={_.partial(this.renderErrorPage, 403)}
                      />
                      <Route
                        path={`*/${ROUTE_PATH.ERROR404}`}
                        component={_.partial(this.renderErrorPage, 404)}
                      />
                      <Route path='/reports' render={this.renderReports} />
                      <RouteWithPermission
                        path='/accounts'
                        permissionAware={hasFuncs(Permission.ACCOUNT_READ)}
                        redirectPath={`/accounts/${ROUTE_PATH.ERROR403}`}
                        render={this.renderAccount}
                      />
                      <RouteWithPermission
                        path='/oem-accounts'
                        permissionAware={hasFuncs(Permission.ACCOUNT_READ)}
                        redirectPath={`/oem-accounts/${ROUTE_PATH.ERROR403}`}
                        render={this.renderOEMAccount}
                      />
                      <RouteWithPermission
                        path='/agencies'
                        permissionAware={hasFuncs(Permission.AGENCY_READ)}
                        redirectPath={`/agencies/${ROUTE_PATH.ERROR403}`}
                        render={this.renderAgencies}
                      />
                      <RouteWithPermission
                        path='/orders'
                        permissionAware={hasFuncs(Permission.ORDER_READ).and(
                          notRoles(beaconAdmin, beaconExecutive)
                        )}
                        redirectPath={`/orders/${ROUTE_PATH.ERROR403}`}
                        render={this.renderOrders}
                      />
                      <RouteWithPermission
                        exact
                        path='/creatives/:creativeId/redirectToDetail'
                        permissionAware={hasFuncs(Permission.CREATIVE_READ).and(
                          notRoles(beaconAdmin, beaconExecutive)
                        )}
                        redirectPath={`/${ROUTE_PATH.ERROR403}`}
                        render={this.renderCreativeDetail}
                      />
                      <RouteWithPermission
                        path='/creatives'
                        permissionAware={hasFuncs(Permission.CREATIVE_READ).and(
                          notRoles(
                            sysAdmin,
                            agencyReport,
                            adsReport,
                            beaconAdmin,
                            beaconExecutive
                          )
                        )}
                        redirectPath={`/creatives/${ROUTE_PATH.ERROR403}`}
                        render={this.renderCreatives}
                      />
                      <RouteWithPermission
                        path='/advertisers'
                        permissionAware={isRoles(
                          sysAdmin,
                          agencyAdmin,
                          agencyOperator
                        )}
                        redirectPath={`/advertisers/${ROUTE_PATH.ERROR403}`}
                        render={this.renderAdvertisers}
                      />
                      <Route
                        path='/audience-segments'
                        render={this.renderAudienceSegments}
                      />
                      <RouteWithPermission
                        exact
                        path='/tenmax-segments'
                        permissionAware={isRoles(RoleNames.sysAdmin)}
                        redirectPath={`/tenmax-segments/${ROUTE_PATH.ERROR403}`}
                        render={this.redirectToSource}
                      />
                      <RouteWithPermission
                        path='/tenmax-segments/sources'
                        permissionAware={isRoles(RoleNames.sysAdmin)}
                        redirectPath={`/tenmax-segments/sources/${ROUTE_PATH.ERROR403}`}
                        render={this.renderTenmaxSegments}
                      />
                      <RouteWithPermission
                        path='/device-management'
                        permissionAware={isRoles(RoleNames.sysAdmin)}
                        redirectPath={`/device-management/${ROUTE_PATH.ERROR403}`}
                        render={this.renderDeviceManagement}
                      />
                      <RouteWithPermission
                        path='/conversions'
                        permissionAware={hasFuncs(Permission.CONVERSION_READ)}
                        redirectPath={`/conversions/${ROUTE_PATH.ERROR403}`}
                        render={this.renderConversions}
                      />
                      <RouteWithPermission
                        path='/campaigns/:campaignId/redirectToDetail'
                        permissionAware={hasFuncs(
                          Permission.ORDER_READ,
                          Permission.CAMPAIGN_READ
                        ).and(notRoles(beaconAdmin, beaconExecutive))}
                        redirectPath={`/${ROUTE_PATH.ERROR403}`}
                        render={this.renderCampaignDetail}
                      />
                      <RouteWithPermission
                        path='/campaign-groups/:l1ObjectId/redirectToDetail'
                        permissionAware={hasFuncs(
                          Permission.ORDER_READ,
                          Permission.CAMPAIGN_READ
                        ).and(notRoles(beaconAdmin, beaconExecutive))}
                        redirectPath={`/${ROUTE_PATH.ERROR403}`}
                        render={this.renderL1ObjectDetail}
                      />
                      <RouteWithPermission
                        path='/bindings/:bindingId/redirectToDetail'
                        permissionAware={hasFuncs(
                          Permission.ORDER_READ,
                          Permission.CAMPAIGN_READ,
                          Permission.CREATIVE_READ
                        ).and(notRoles(beaconAdmin, beaconExecutive))}
                        redirectPath={`/${ROUTE_PATH.ERROR403}`}
                        render={this.renderBindingDetail}
                      />
                      <RouteWithPermission
                        path='/system/product-groups'
                        permissionAware={isRoles(sysAdmin).or(
                          isRoles(
                            agencyAdmin,
                            agencyManager,
                            agencyOperator
                          ).and(
                            addOnEnabled(ADDONFEATURE.COMPANY.PRODUCT_GROUP)
                          )
                        )}
                        redirectPath={`/system/product-groups/${ROUTE_PATH.ERROR403}`}
                        render={this.renderProductGroups}
                      />
                      <RouteWithPermission
                        path='/system/currency-rate'
                        permissionAware={isRoles(sysAdmin)}
                        redirectPath={`/system/currency-rate/${ROUTE_PATH.ERROR403}`}
                        render={this.renderCurrencyRate}
                      />
                      <RouteWithPermission
                        path='/system/channel-groups'
                        permissionAware={isRoles(sysAdmin)}
                        redirectPath={`/system/channel-groups/${ROUTE_PATH.ERROR403}`}
                        render={this.renderChannelGroups}
                      />
                      <RouteWithPermission
                        path='/system/streaming-status'
                        permissionAware={isRoles(sysAdmin)}
                        redirectPath={`/system/streaming-status/${ROUTE_PATH.ERROR403}`}
                        render={this.renderStreamingStatus}
                      />
                      <RouteWithPermission
                        path='/stored-value'
                        permissionAware={isRoles(sysAdmin).or(
                          isRoles(
                            agencyAdmin,
                            agencyOperator,
                            agencyManager
                          ).and(addOnEnabled(ADDONFEATURE.COMPANY.STORED_VALUE))
                        )}
                        redirectPath={`/stored-value/${ROUTE_PATH.ERROR403}`}
                        render={this.renderStoredValue}
                      />
                      <RouteWithPermission
                        path='/custom-layouts'
                        permissionAware={isRoles(sysAdmin)}
                        redirectPath={`/custom-layouts/${ROUTE_PATH.ERROR403}`}
                        render={this.renderCustomLayouts}
                      />
                      <RouteWithPermission
                        path='/pmp'
                        permissionAware={isRoles(sysAdmin)}
                        redirectPath={`/pmp/${ROUTE_PATH.ERROR403}`}
                        render={this.renderPmp}
                      />
                      <RouteWithPermission
                        path='/beacon-messages'
                        permissionAware={isRoles(beaconAdmin, beaconExecutive)}
                        redirectPath={`/beacon-messages/${ROUTE_PATH.ERROR403}`}
                        render={this.renderBeaconMessages}
                      />
                      <RouteWithPermission
                        path='/merchants'
                        permissionAware={isRoles(beaconAdmin, beaconExecutive)}
                        redirectPath={`/merchants/${ROUTE_PATH.ERROR403}`}
                        render={this.renderMerchants}
                      />
                      <RouteWithPermission
                        path='/restaurants'
                        permissionAware={isRoles(beaconAdmin, beaconExecutive)}
                        redirectPath={`/restaurants/${ROUTE_PATH.ERROR403}`}
                        render={this.renderRestaurants}
                      />
                      <RouteWithPermission
                        path='/site-announcements'
                        permissionAware={hasFuncs(
                          Permission.SITE_ANNOUNCEMENT_LIST_READ
                        )}
                        redirectPath={`/site-announcements/${ROUTE_PATH.ERROR403}`}
                        render={this.renderSiteAnnouncements}
                      />
                      <Route path='/api-swagger' render={this.renderSwagger} />
                      <Route render={this.renderMainPage} />
                    </Switch>
                  </Fragment>
                </ElementScrollControl>
              </div>
            </Fragment>
          )}
        </div>
      </div>
    );
  };

  render() {
    const model = this.props.model;
    return (
      <MenuBarExpandContext.Provider
        value={this.props.model.barModel.state.expanded}
      >
        <PermissionContext.Provider value={model.state.permissionContext}>
          <CoreContext.Provider value={model.core}>
            <Switch>
              <Route
                path='/final-report'
                render={this.renderFinalReportPageContent}
              />
              <Route render={this.renderMainPageContent} />
            </Switch>
          </CoreContext.Provider>
        </PermissionContext.Provider>
      </MenuBarExpandContext.Provider>
    );
  }
}
